import { createChat } from "../resources/api.resource.ts";
import { MemberOption, UserMapValue } from "../models/User.ts";
import { MessageItem, CollaborationInstance } from "../models/LeftPanel.ts";

interface CreateChatResponse {
  status: string;
  message: string;
  collaborationId: string;
  activeCollabInstanceId: string | null;
  pubsubGroupId: CollaborationInstance | null;
  collabInstanceId: CollaborationInstance | null;
}

interface CreateChatItemResult {
  item: MessageItem;
  response: CreateChatResponse;
  isExisting: boolean;
  chatType: "direct" | "group";
}

export async function createChatItem(args: {
  type: "dm" | "group";
  members: MemberOption[];
  currentUser: { userId: string; name?: string };
  allUsersMap: Record<string, UserMapValue>;
}): Promise<CreateChatItemResult> {
  const { type, members, currentUser, allUsersMap } = args;
  const memberUids = members.map((m) => m.uid);
  const memberDetails = memberUids.map((uid) => ({
    uid,
    name: allUsersMap[uid.toLowerCase()]?.name || "Unknown",
    email: allUsersMap[uid.toLowerCase()]?.email || "unknown@example.com",
    userType: allUsersMap[uid.toLowerCase()]?.userType || "user",
  }));

  const payload: Record<string, unknown> = { type: type === "dm" ? "dm" : "group", memberUids, memberDetails };
  if (type === "group") {
    const allNames = [currentUser.name || "Me", ...members.map((m) => m.name)];
    payload.name = allNames.join(",");
  }

  const response: CreateChatResponse = await createChat(payload);
  const chatType: "direct" | "group" = type === "dm" ? "direct" : "group";

  if (!response.collabInstanceId) {
    // DM/group already exists — caller must locate item in leftPanelData by collaborationIdSummary
    const item: MessageItem = {
      collaborationId: response.collaborationId,
      collaborationIdSummary: response.collaborationId,
      name: members[0]?.name || "Unknown",
      notificationCount: 0,
    };
    return { item, response, isExisting: true, chatType };
  }

  const instance = response.collabInstanceId;
  const currentUid = currentUser.userId;

  let name: string;
  let iconName: string;
  if (type === "dm") {
    const otherUid = members[0]?.uid;
    name = allUsersMap[otherUid?.toLowerCase()]?.name || members[0]?.name || "Unknown";
    iconName = "PersonIcon";
  } else {
    const allNames = [currentUser.name || "Me", ...members.map((m) => m.name)];
    name = allNames.join(",");
    iconName = "GroupsIcon";
  }

  const item: MessageItem = {
    collaborationId: instance.id,
    collaborationIdSummary: response.collaborationId,
    name,
    iconName,
    notificationCount: 0,
    unread: false,
    memberUids: [currentUid, ...memberUids],
    collab_instances: [instance],
  };

  return { item, response, isExisting: false, chatType };
}

// Backwards-compat wrapper kept for any remaining callers
export async function createDmChat(
  member: MemberOption,
  allUsersMap: Record<string, UserMapValue>,
  currentUser?: { userId: string; name?: string }
): Promise<MessageItem> {
  const result = await createChatItem({
    type: "dm",
    members: [member],
    currentUser: currentUser || { userId: "" },
    allUsersMap,
  });
  return result.item;
}
